import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApolloClient } from "@apollo/client";

import {
  SELECTED_LANGUAGE_KEY,
  updateSelectedLanguage,
} from "../../utils/i18n/utils";
import { ExtendedApolloClient } from "../../apollo";
import { isNotNull } from "common/utils/tools";
import { SUPPORTED_LANGUAGES } from "common/constants";
import { buildLink } from "common/routing";
import { accountTitle, useIsMobileWidth, useIsTabletWidth } from "./utils";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { AuthContext } from "../Authorization/AuthContext";
import { useOutsideAlerter } from "../../utils/outsideAlerter";
import { DropdownMenu } from "../Inputs";
import { Button } from "../Common/Button";
import { ActionsProps } from "../Inputs/DropdownMenu";
import { getCurrentURL } from "../../utils/url";
import { Icon } from "../Common/Icons/LucideIcons";
import { Body } from "../Common/Typography";
import AITranslationWarningBanner from "../Common/AITranslationWarningBanner";

import {
  AccountLogo,
  FooterContainer,
  HeaderContainer,
  HeaderContent,
  MenuSection,
  MenuParent,
  Page,
  HomeButton,
  LanguageButtonWrapper,
  MenuRow,
  GUEST_WIDTH,
} from "./__styles__/Layout";
import { isEmpty } from "lodash";

const LANGUAGE_MAPPER = {
  en: "English",
  es: "Español",
};

const LanguageButton = ({ inMenu = false }: { inMenu?: boolean }) => {
  const { i18n } = useTranslation();
  const { setValue } = useLocalStorage({ key: SELECTED_LANGUAGE_KEY });
  const client = useApolloClient() as ExtendedApolloClient;
  const account = useContext(AuthContext).account!;
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);

  const supportedLanguages = account.publicPortal.supportedLanguages!;
  if (supportedLanguages!.length === 1) return null;

  const languageActions = supportedLanguages
    .map(language => {
      if (language === i18n.language) return null;
      return {
        label: LANGUAGE_MAPPER[language],
        onClick: () =>
          updateSelectedLanguage({
            language,
            updateStaticTranslations: i18n.changeLanguage,
            updateDynamicTranslations: client.updateLanguage,
            updateLocalStorage: () => setValue,
          }),
      };
    })
    .filter(isNotNull);

  const selectedLanguageCopy =
    LANGUAGE_MAPPER[i18n.language as SUPPORTED_LANGUAGES];

  return inMenu ? (
    <MenuSection>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
      >
        <Button
          onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
          size="medium"
          leftIconName="globe"
          styleVariant="buttonLinkDark"
        >
          {selectedLanguageCopy}
        </Button>
        <Icon
          iconName={languageMenuOpen ? "chevron-up" : "chevron-down"}
          size={16}
          color="contentPrimaryDark"
        />
      </div>
      {languageMenuOpen &&
        languageActions.map((action, index) => (
          <Button
            key={index}
            onClick={action.onClick!}
            styleVariant="buttonLinkDark"
            size="medium"
            style={{ paddingLeft: "24px" }}
          >
            {action.label}
          </Button>
        ))}
    </MenuSection>
  ) : (
    <LanguageButtonWrapper>
      <DropdownMenu
        actions={languageActions}
        customButton={({ onClick }) => (
          <Button
            onClick={onClick}
            size="medium"
            leftIconName="globe"
            rightIconName="chevron-down"
            styleVariant="buttonLinkDark"
          >
            {selectedLanguageCopy}
          </Button>
        )}
        position="below-right"
      />
    </LanguageButtonWrapper>
  );
};

const CertificateButton = ({ inMenu = false }: { inMenu?: boolean }) => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { account, supportsElevationCertificates } = useContext(AuthContext);

  const supportElevationCertificateUploads =
    account?.publicPortal.supportsDocumentUploads;

  const actions: Array<ActionsProps> = [];

  if (supportsElevationCertificates) {
    actions.push({
      label: supportElevationCertificateUploads
        ? t("dropdown-view-elevation-certificates")
        : t("button-elevation-certificates"),
      onClick: () =>
        history.push(
          buildLink("guestElevationCertificates", { accountId: account?.id })
        ),
    });
  }

  if (supportElevationCertificateUploads) {
    actions.push({
      label: t("dropdown-upload-elevation-certificates"),
      onClick: () =>
        history.push(
          buildLink("guestUploadElevationCertificates", {
            accountId: account.id,
          }),
          { prevLocation: getCurrentURL(location) }
        ),
    });
  }

  if (isEmpty(actions)) return null;

  return inMenu || actions.length === 1 ? (
    <>
      {actions.map((action, index) => (
        <Button
          key={index}
          onClick={action.onClick}
          styleVariant="buttonLinkDark"
          size="medium"
        >
          {action.label}
        </Button>
      ))}
    </>
  ) : (
    <DropdownMenu
      actions={actions}
      customButton={({ onClick }) => (
        <Button
          styleVariant="buttonLinkDark"
          size="medium"
          onClick={onClick}
          rightIconName="chevron-down"
        >
          {t("button-elevation-certificates")}
        </Button>
      )}
      position="below-right"
    />
  );
};

const GetHelpButton = ({
  inMenu = false,
  propertyId,
}: {
  inMenu?: boolean;
  propertyId?: string;
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const account = useContext(AuthContext).account!;

  const onGetHelpClick = () =>
    history.push(
      buildLink("getHelp", { accountId: account.id }, { propertyId })
    );

  const style = inMenu
    ? undefined
    : { minWidth: "unset", whiteSpace: "nowrap" as const };

  return (
    <Button
      onClick={onGetHelpClick}
      styleVariant={inMenu ? "buttonLinkDark" : "secondary"}
      size="medium"
      leftIconName={inMenu ? undefined : "info"}
      style={style}
    >
      {t("get-help")}
    </Button>
  );
};

const HeaderRowMenu = ({ propertyId }: { propertyId?: string }) => {
  return (
    <>
      <MenuRow>
        <CertificateButton />
        <GetHelpButton propertyId={propertyId} />
      </MenuRow>
      <LanguageButton />
    </>
  );
};

const ExpandedMenu = ({ propertyId }: { propertyId?: string }) => {
  const account = useContext(AuthContext).account!;

  return (
    <MenuParent style={{ background: account.publicPortal.color }}>
      <MenuSection>
        <CertificateButton inMenu />
        <GetHelpButton inMenu propertyId={propertyId} />
      </MenuSection>
      <LanguageButton inMenu />
    </MenuParent>
  );
};

const MenuButton = ({ onClick }: { onClick: () => void }) => (
  <Icon
    iconName="align-justify"
    onClick={onClick}
    size={16}
    color="contentPrimaryDark"
    testId="menu-button"
  />
);

interface HeaderProps {
  propertyId?: string;
}

export const Header = ({ propertyId }: HeaderProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapperRef = useRef(null);
  const account = useContext(AuthContext).account!;
  const isMobileScreen = useIsMobileWidth();
  const isTabletScreen = useIsTabletWidth();

  const isSmallScreen = isMobileScreen || isTabletScreen;

  useEffect(() => {
    setMenuOpen(false);
  }, [isSmallScreen]);

  // when someone clicks outside of the mobile menu the menu will disappear
  useOutsideAlerter({
    ref: wrapperRef,
    onOutsideInteraction: () => setMenuOpen(false),
  });

  const subtitle = account.publicPortal.navBarTitle;

  return (
    <>
      <HeaderContainer
        style={{ background: account.publicPortal.color }}
        ref={wrapperRef}
        id="header"
      >
        <HeaderContent>
          <HomeButton to={buildLink("guestHome", { accountId: account.id })}>
            {account.logoUrl && (
              <AccountLogo src={account.logoUrl} alt={`${account.name} Logo`} />
            )}
            <div>
              <Body
                testId="resident-main-header"
                size="large"
                type="emphasis"
                color="contentPrimaryDark"
              >
                {accountTitle()}
              </Body>
              <Body
                testId="resident-subheader"
                size="small"
                type="regular"
                color="contentPrimaryDark"
              >
                {subtitle}
              </Body>
            </div>
          </HomeButton>
          {isSmallScreen ? (
            <MenuButton onClick={() => setMenuOpen(!menuOpen)} />
          ) : (
            <HeaderRowMenu propertyId={propertyId} />
          )}
        </HeaderContent>
        {menuOpen && <ExpandedMenu propertyId={propertyId} />}
      </HeaderContainer>
      <AITranslationWarningBanner />
    </>
  );
};

export const Footer = () => {
  const { t } = useTranslation();
  return (
    <FooterContainer id="footer">
      <Body size="default" type="regular" color="contentPlaceholder">
        <a href="https://www.withforerunner.com/" target="_blank">
          {t("footer-forerunner")}
        </a>{" "}
        |{" "}
        <a
          href="https://www.withforerunner.com/legal/terms-of-services"
          target="_blank"
        >
          {t("footer-terms")}
        </a>
      </Body>
    </FooterContainer>
  );
};

type LayoutProps = {
  propertyId?: string;
  hasGutters?: boolean;
  children: ReactNode;
};

const Layout = ({ propertyId, children, hasGutters = false }: LayoutProps) => {
  const childrenDisplay = hasGutters ? (
    <div
      style={{ maxWidth: GUEST_WIDTH, marginLeft: "auto", marginRight: "auto" }}
    >
      {children}
    </div>
  ) : (
    children
  );
  return (
    <Page>
      <Header propertyId={propertyId} />
      {childrenDisplay}
      <Footer />
    </Page>
  );
};

export default Layout;
